import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.yyzprep.ca';

// Format time for display
const formatTime = (date) => {
  return new Date(date).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// Audio pool for overlapping sounds
const createAudioPool = (src, poolSize = 3) => {
  const pool = [];
  for (let i = 0; i < poolSize; i++) {
    const audio = new Audio(src);
    audio.volume = 0.8;
    audio.preload = 'auto';
    audio.load();
    pool.push(audio);
  }
  let index = 0;
  return {
    play: () => {
      const audio = pool[index];
      audio.currentTime = 0;
      audio.play().catch(() => {});
      index = (index + 1) % poolSize;
    }
  };
};

function PDAScanner() {
  const [lastScans, setLastScans] = useState([]);
  const [todayCount, setTodayCount] = useState(0);
  const [flashColor, setFlashColor] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [failedCount, setFailedCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [currentBuffer, setCurrentBuffer] = useState('');
  const [scannerStatus, setScannerStatus] = useState('ready'); // 'ready', 'receiving', 'processing'

  const successPool = useRef(null);
  const errorPool = useRef(null);
  const lastScanRef = useRef({ code: '', time: 0 });
  const flashTimeoutRef = useRef(null);
  const bufferRef = useRef('');
  const bufferTimeoutRef = useRef(null);
  const hiddenInputRef = useRef(null);
  const isComposingRef = useRef(false);
  const lastInputTimeRef = useRef(0);
  const processedCodesRef = useRef(new Set()); // Prevent duplicate processing

  // Switch manifest for PWA installation
  useEffect(() => {
    const originalManifest = document.querySelector('link[rel="manifest"]');
    const originalHref = originalManifest?.href;

    if (originalManifest) {
      originalManifest.href = '/pda-manifest.json';
    }

    return () => {
      if (originalManifest && originalHref) {
        originalManifest.href = originalHref;
      }
    };
  }, []);

  // Initialize audio pools and fetch count
  useEffect(() => {
    successPool.current = createAudioPool('/sounds/success-scan.mp3', 4);
    errorPool.current = createAudioPool('/sounds/error-scan.mp3', 2);
    fetchTodayCount();
  }, []);

  // Keep hidden input focused for Android PDA scanners
  const focusHiddenInput = useCallback(() => {
    if (hiddenInputRef.current && document.activeElement !== hiddenInputRef.current) {
      setTimeout(() => {
        hiddenInputRef.current?.focus({ preventScroll: true });
      }, 50);
    }
  }, []);

  // Focus on mount and when visibility changes
  useEffect(() => {
    focusHiddenInput();

    // Re-focus when page becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        focusHiddenInput();
      }
    };

    // Re-focus when window gets focus
    const handleWindowFocus = () => {
      focusHiddenInput();
    };

    // Re-focus on touch (for Android)
    const handleTouch = (e) => {
      // Don't refocus if touching a button
      if (e.target.tagName !== 'BUTTON') {
        focusHiddenInput();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('touchend', handleTouch, { passive: true });

    // Periodic focus check for stubborn Android WebViews
    const focusInterval = setInterval(focusHiddenInput, 3000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('touchend', handleTouch);
      clearInterval(focusInterval);
    };
  }, [focusHiddenInput]);

  // Clear the buffer and input
  const clearBuffer = useCallback(() => {
    bufferRef.current = '';
    setCurrentBuffer('');
    if (hiddenInputRef.current) {
      hiddenInputRef.current.value = '';
    }
    setScannerStatus('ready');
  }, []);

  // Process scanned code - called from multiple input sources
  const processScanCode = useCallback((code) => {
    const trimmed = code.trim().replace(/[\r\n\t\x00-\x1F]/g, ''); // Remove control chars

    if (!trimmed || trimmed.length < 3) {
      clearBuffer();
      return;
    }

    // Dedupe check - prevent same code processed within 500ms
    const now = Date.now();
    const codeKey = `${trimmed}-${Math.floor(now / 500)}`;
    if (processedCodesRef.current.has(codeKey)) {
      clearBuffer();
      return;
    }
    processedCodesRef.current.add(codeKey);

    // Clean up old keys after 2 seconds
    setTimeout(() => {
      processedCodesRef.current.delete(codeKey);
    }, 2000);

    handleScan(trimmed);
    clearBuffer();
  }, [clearBuffer]);

  // Handle hidden input changes - primary method for Android PDA scanners
  const handleHiddenInputChange = useCallback((e) => {
    const value = e.target.value || '';
    const now = Date.now();

    // Clear pending timeout
    if (bufferTimeoutRef.current) {
      clearTimeout(bufferTimeoutRef.current);
    }

    // Update display
    bufferRef.current = value;
    setCurrentBuffer(value);
    setScannerStatus(value ? 'receiving' : 'ready');

    // Check for newline/carriage return (scanner typically sends Enter at end)
    if (value.includes('\n') || value.includes('\r')) {
      const code = value.replace(/[\r\n]/g, '');
      processScanCode(code);
      return;
    }

    // Calculate timeout based on input speed
    const timeSinceLastInput = now - lastInputTimeRef.current;
    lastInputTimeRef.current = now;

    // Fast input (< 30ms between chars) = scanner, use shorter timeout
    // Slow input = manual typing, use longer timeout
    const isLikelyScanner = timeSinceLastInput < 30 && value.length > 1;
    const timeoutDelay = isLikelyScanner ? 100 : 400;

    // Auto-process after timeout if we have enough characters
    bufferTimeoutRef.current = setTimeout(() => {
      const currentValue = hiddenInputRef.current?.value || bufferRef.current;
      if (currentValue.trim().length >= 5) {
        processScanCode(currentValue);
      } else {
        // Not enough chars, just clear
        clearBuffer();
      }
    }, timeoutDelay);
  }, [processScanCode, clearBuffer]);

  // Handle Enter key on hidden input
  const handleHiddenInputKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (bufferTimeoutRef.current) {
        clearTimeout(bufferTimeoutRef.current);
      }
      const value = e.target.value || bufferRef.current;
      if (value.trim()) {
        processScanCode(value);
      }
    }
  }, [processScanCode]);

  // Handle paste events (some scanners paste directly)
  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const pastedText = e.clipboardData?.getData('text') || '';
    if (pastedText.trim()) {
      processScanCode(pastedText);
    }
  }, [processScanCode]);

  // Handle composition events (for IME-based input on some Android devices)
  const handleCompositionStart = useCallback(() => {
    isComposingRef.current = true;
  }, []);

  const handleCompositionEnd = useCallback((e) => {
    isComposingRef.current = false;
  }, []);

  // Fallback: Listen for key events on document (for scanners that don't focus input)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Skip if composing or if hidden input is handling it
      if (isComposingRef.current) return;
      if (document.activeElement === hiddenInputRef.current) return;

      // Ignore modifier keys
      if (e.ctrlKey || e.altKey || e.metaKey) return;

      if (e.key === 'Enter') {
        e.preventDefault();
        const code = bufferRef.current.trim();
        if (code) {
          processScanCode(code);
        }
        return;
      }

      // Only accept printable characters
      if (e.key.length === 1) {
        e.preventDefault();
        bufferRef.current += e.key;
        setCurrentBuffer(bufferRef.current);
        setScannerStatus('receiving');

        // Clear buffer timeout
        if (bufferTimeoutRef.current) clearTimeout(bufferTimeoutRef.current);
        bufferTimeoutRef.current = setTimeout(() => {
          if (bufferRef.current.trim().length >= 5) {
            processScanCode(bufferRef.current);
          } else {
            clearBuffer();
          }
        }, 400);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [processScanCode, clearBuffer]);

  const fetchTodayCount = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/delivery-receiving/log`);
      if (response.data.success) {
        setTodayCount(response.data.entries?.length || 0);
      }
    } catch (error) {
      console.error('Error fetching count:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Retry a failed scan
  const retryScan = useCallback(async (code, scanId) => {
    setLastScans(prev => prev.map(scan =>
      scan.id === scanId
        ? { ...scan, pending: true, success: true, message: '' }
        : scan
    ));
    setFailedCount(prev => prev - 1);
    setPendingCount(prev => prev + 1);

    try {
      const response = await axios.post(`${API_URL}/api/delivery-receiving/scan`, { tracking_code: code });

      if (response.data.success) {
        successPool.current?.play();
        const isDuplicate = response.data.duplicate;

        setLastScans(prev => prev.map(scan =>
          scan.id === scanId
            ? { ...scan, success: true, duplicate: isDuplicate, pending: false }
            : scan
        ));

        if (!isDuplicate) {
          setTodayCount(prev => prev + 1);
        }
      } else {
        errorPool.current?.play();
        setLastScans(prev => prev.map(scan =>
          scan.id === scanId
            ? { ...scan, success: false, pending: false, message: response.data.message || 'Failed' }
            : scan
        ));
        setFailedCount(prev => prev + 1);
      }
    } catch (error) {
      errorPool.current?.play();
      setLastScans(prev => prev.map(scan =>
        scan.id === scanId
          ? { ...scan, success: false, pending: false, message: 'Network error' }
          : scan
      ));
      setFailedCount(prev => prev + 1);
    } finally {
      setPendingCount(prev => prev - 1);
    }
  }, []);

  const clearFailed = useCallback(() => {
    setLastScans(prev => prev.filter(scan => scan.success || scan.pending));
    setFailedCount(0);
  }, []);

  const retryAllFailed = useCallback(() => {
    const failedScans = lastScans.filter(scan => !scan.success && !scan.pending);
    failedScans.forEach(scan => {
      retryScan(scan.code, scan.id);
    });
  }, [lastScans, retryScan]);

  const handleScan = useCallback((trackingCode) => {
    const code = trackingCode.trim();
    if (!code) return;

    const now = Date.now();
    const last = lastScanRef.current;

    // Debounce: ignore same code within 300ms
    if (code === last.code && now - last.time < 300) {
      return;
    }

    lastScanRef.current = { code, time: now };
    const scanId = `${code}-${now}`;

    // Show pending state (NO sound yet - wait for API confirmation)
    if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
    setFlashColor('blue');

    // Add to list as pending
    setLastScans(prev => [{
      id: scanId,
      code,
      success: true,
      pending: true,
      time: new Date()
    }, ...prev].slice(0, 10));

    setPendingCount(prev => prev + 1);

    // API call - sound only plays AFTER database confirms save
    axios.post(`${API_URL}/api/delivery-receiving/scan`, { tracking_code: code })
      .then(response => {
        setPendingCount(prev => prev - 1);

        if (response.data.success) {
          // SUCCESS: Now play sound and flash green
          successPool.current?.play();
          setFlashColor('green');
          flashTimeoutRef.current = setTimeout(() => setFlashColor(null), 250);

          const isDuplicate = response.data.duplicate;

          setLastScans(prev => prev.map(scan =>
            scan.id === scanId
              ? { ...scan, success: true, duplicate: isDuplicate, pending: false }
              : scan
          ));

          if (!isDuplicate) {
            setTodayCount(prev => prev + 1);
          }
        } else {
          // FAILURE: Play error sound
          errorPool.current?.play();
          setLastScans(prev => prev.map(scan =>
            scan.id === scanId
              ? { ...scan, success: false, pending: false, message: response.data.message || 'Failed' }
              : scan
          ));
          setFailedCount(prev => prev + 1);
          setFlashColor('red');
          setTimeout(() => setFlashColor(null), 300);
        }
      })
      .catch(error => {
        // NETWORK ERROR: Play error sound
        setPendingCount(prev => prev - 1);
        errorPool.current?.play();
        setLastScans(prev => prev.map(scan =>
          scan.id === scanId
            ? { ...scan, success: false, pending: false, message: 'Network error' }
            : scan
        ));
        setFailedCount(prev => prev + 1);
        setFlashColor('red');
        setTimeout(() => setFlashColor(null), 300);
      });
  }, []);

  // Background color
  const bgColor = flashColor === 'green'
    ? 'bg-green-500'
    : flashColor === 'red'
      ? 'bg-red-500'
      : flashColor === 'blue'
        ? 'bg-blue-500'
        : 'bg-gray-900';

  return (
    <div className={`min-h-screen ${bgColor} transition-colors duration-150 flex flex-col p-4 select-none`}>
      {/* Hidden input for Android PDA scanner capture - CRITICAL for barcode scanner support */}
      <input
        ref={hiddenInputRef}
        type="text"
        inputMode="none"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        className="absolute opacity-0 pointer-events-none"
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 0,
          width: '1px',
          height: '1px'
        }}
        onChange={handleHiddenInputChange}
        onKeyDown={handleHiddenInputKeyDown}
        onPaste={handlePaste}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        onBlur={focusHiddenInput}
        aria-hidden="true"
        tabIndex={-1}
      />

      {/* Error Banner */}
      {failedCount > 0 && (
        <div className="mb-4 bg-red-600 text-white rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl">⚠️</span>
            <span className="font-bold">{failedCount} FAILED</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={retryAllFailed}
              className="bg-white text-red-600 px-3 py-1 rounded font-bold text-sm active:bg-gray-200"
            >
              RETRY ALL
            </button>
            <button
              onClick={clearFailed}
              className="bg-red-800 text-white px-3 py-1 rounded font-bold text-sm active:bg-red-900"
            >
              CLEAR
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-white">DELIVERY SCANNER</h1>
        <div className="flex items-center gap-2">
          {pendingCount > 0 && (
            <div className="bg-blue-500/50 rounded-lg px-3 py-2">
              <span className="text-white text-sm font-bold">{pendingCount}</span>
              <span className="text-white/70 text-xs ml-1">sync</span>
            </div>
          )}
          <div className="bg-white/20 rounded-lg px-4 py-2">
            <span className="text-white text-lg font-bold">
              {isLoading ? '...' : todayCount}
            </span>
            <span className="text-white/70 text-sm ml-1">today</span>
          </div>
        </div>
      </div>

      {/* Scan Ready Indicator */}
      <div
        className={`mb-4 rounded-xl p-6 text-center border-2 border-dashed transition-all duration-200 ${
          currentBuffer
            ? 'bg-blue-500/20 border-blue-400/50'
            : 'bg-white/10 border-white/30'
        }`}
        onClick={focusHiddenInput}
      >
        {currentBuffer ? (
          <div>
            <div className="text-blue-300 text-sm mb-1 animate-pulse">RECEIVING SCAN...</div>
            <div className="text-white text-2xl font-mono font-bold truncate">
              {currentBuffer}
            </div>
            <div className="text-white/40 text-xs mt-2">
              {currentBuffer.length} characters
            </div>
          </div>
        ) : (
          <div>
            <div className="text-6xl mb-2">📦</div>
            <div className="text-white text-xl font-bold">READY TO SCAN</div>
            <div className="text-white/50 text-sm mt-1">Point scanner at barcode</div>
            <div className="text-white/30 text-xs mt-2">Tap here if scanner stops working</div>
          </div>
        )}
      </div>

      {/* Last Scans */}
      <div className="flex-1 space-y-2 overflow-auto">
        {lastScans.length === 0 ? (
          <div className="text-center text-white/30 py-4">
            <p className="text-sm">Scans will appear here</p>
          </div>
        ) : (
          lastScans.map((scan) => (
            <div
              key={scan.id}
              onClick={() => !scan.success && !scan.pending && retryScan(scan.code, scan.id)}
              className={`flex items-center justify-between p-3 rounded-lg ${
                scan.pending
                  ? 'bg-blue-600/30 border border-blue-400/50'
                  : scan.success
                    ? 'bg-green-600/30 border border-green-400/50'
                    : 'bg-red-600/50 border-2 border-red-400 cursor-pointer active:bg-red-700/50'
              }`}
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <span className={`text-2xl flex-shrink-0 ${
                  scan.pending
                    ? 'text-blue-400'
                    : scan.success
                      ? 'text-green-400'
                      : 'text-red-400'
                }`}>
                  {scan.pending ? '⏳' : scan.success ? '✓' : '✗'}
                </span>
                <div className="min-w-0 flex-1">
                  <span className="text-white font-mono text-sm truncate block">
                    {scan.code}
                  </span>
                  {!scan.success && !scan.pending && scan.message && (
                    <span className="text-red-300 text-xs">{scan.message} - tap to retry</span>
                  )}
                </div>
                {scan.duplicate && (
                  <span className="text-yellow-300 text-xs flex-shrink-0">(dup)</span>
                )}
              </div>
              <span className="text-white/60 text-sm flex-shrink-0 ml-2">
                {formatTime(scan.time)}
              </span>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="mt-4 text-center">
        {failedCount > 0 ? (
          <div className="text-red-400 text-sm font-bold">
            ⚠️ {failedCount} failed - tap to retry
          </div>
        ) : pendingCount > 0 ? (
          <div className="text-blue-400 text-sm">
            Syncing {pendingCount}...
          </div>
        ) : (
          <div className="text-white/40 text-xs">
            Use scanner trigger to scan
          </div>
        )}
      </div>
    </div>
  );
}

export default PDAScanner;
